/* eslint-disable @typescript-eslint/no-explicit-any */
import { useIntl, IntlShape } from "react-intl";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import VitalsIcon from "../../components/VitalsIcon";

interface VitalsBarV2Props {
  vitals: string | undefined | null; // JSON string (vitalsByDate)
  patientName: string | undefined | null; // Just a string
  patientBioData: string | undefined | null; // JSON string (bioData)
}

function VitalsBarV2({
  vitals,
  patientName,
  patientBioData,
}: VitalsBarV2Props) {
  const intl = useIntl();

  // Parse JSON strings safely
  let parsedVitals: Record<string, any> = {};
  let parsedPatientBioData: Record<string, any> = {};
  const parsedPatientName: string = patientName || "Unknown";

  try {
    parsedVitals = vitals ? JSON.parse(vitals) : {};
  } catch (error) {
    console.error("Error parsing vitals JSON:", error);
  }

  try {
    parsedPatientBioData = patientBioData ? JSON.parse(patientBioData) : {};
  } catch (error) {
    console.error("Error parsing patientBioData JSON:", error);
  }

  // Extract most recent vitals from vitalsByDate
  console.log("Parsed vitals: ", parsedVitals);
  const latestVitals = getLatestVitals(parsedVitals || {});

  return (
    <Box marginX="2em">
      <Stack
        direction="row"
        spacing="auto"
        alignItems="center"
        sx={{ mx: "1em", marginTop: "1em" }}
      >
        <PatientInfoPanel
          name={parsedPatientName}
          bioData={parsedPatientBioData}
        />
        {vitalsInfo(latestVitals, intl).map((vital) => (
          <VitalInfoPanel {...vital} key={vital.title} />
        ))}
      </Stack>
    </Box>
  );
}

interface PatientInfoPanelProps {
  name: string;
  bioData: Record<string, any>;
}

function PatientInfoPanel({ name, bioData }: PatientInfoPanelProps) {
  return (
    <Stack direction="row" spacing="6px">
      <Typography fontWeight="bold" fontSize="2em">
        {name}
      </Typography>
      {bioData?.Gender && (
        <Typography
          fontWeight="bold"
          sx={{ paddingLeft: "2em", alignSelf: "center" }}
        >
          {bioData.Gender}
        </Typography>
      )}
      {bioData?.Age && (
        <Typography
          fontWeight="bold"
          sx={{ paddingLeft: "2em", alignSelf: "center" }}
        >
          {bioData.Age}
        </Typography>
      )}
    </Stack>
  );
}

interface VitalInfoPanelParams {
  title: string;
  value: string | number;
  unit: string;
  alert?: boolean;
}

// Function to extract the most recent vitals
function getLatestVitals(
  vitalsByDate: Record<string, any>
): Record<string, any> {
  if (!vitalsByDate || typeof vitalsByDate !== "object") {
    console.log("returning empty from gt latest vitals");
    return {};
  }

  const dates = Object.keys(vitalsByDate).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  console.log("vitals by date: ", vitalsByDate);

  return dates.length > 0 ? vitalsByDate[dates[1]] : {};
}

function vitalsInfo(
  vitals: Record<string, any>,
  intl: IntlShape
): VitalInfoPanelParams[] {
  console.log("Vitals in info: ", JSON.stringify(vitals));
  return [
    {
      title: intl.formatMessage({ id: "demographics.weight" }),
      value: vitals?.Weight || "N/A",
      unit: intl.formatMessage({ id: "kg" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.height" }),
      value: vitals?.Height || "N/A",
      unit: intl.formatMessage({ id: "cm" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.pulse" }),
      value: vitals?.Pulse || "N/A",
      unit: intl.formatMessage({ id: "BPM" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.blood-pressure" }),
      value: vitals?.BP || "N/A",
      unit: intl.formatMessage({ id: "mmHg" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.temperature" }),
      value: vitals?.Temperature || "N/A",
      unit: intl.formatMessage({ id: "°C" }),
    },
    {
      title: intl.formatMessage({ id: "demographics.bsr" }),
      value: vitals?.BSR || "N/A",
      unit: intl.formatMessage({ id: "mg/dL" }),
    },
  ];
}

function VitalInfoPanel({
  title,
  value,
  unit,
  alert = false,
}: VitalInfoPanelParams) {
  const variant = alert ? "outlined" : "elevation";
  const borderProps = alert
    ? { borderBlockColor: "red", borderInlineColor: "red" }
    : {};

  return (
    <Card
      variant={variant}
      sx={{
        width: "auto",
        paddingInline: "1em",
        borderRadius: "60px",
        ...borderProps,
      }}
    >
      <Stack alignItems="flex-start" mx="6px">
        <Stack
          direction="row"
          alignContent="flex-end"
          alignItems="baseline"
          spacing="5px"
          mx="auto"
        >
          <VitalsIcon />
          <Typography variant="datum">{value}</Typography>
          <Typography variant="caption">{unit}</Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

export default VitalsBarV2;
